import { useState, useEffect, useRef, useCallback } from "react";

const MAX_LINES = 5000;

interface UseLogStreamReturn {
  lines: string[];
  isConnected: boolean;
  isPaused: boolean;
  togglePause: () => void;
  clearLogs: () => void;
}

export function useLogStream(): UseLogStreamReturn {
  const [lines, setLines] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const pausedBufferRef = useRef<string[]>([]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const connect = useCallback(() => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/api/logs/stream`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setIsConnected(true);
      if (pausedBufferRef.current.length > 0) {
        setLines((prev) => {
          const next = [...prev, ...pausedBufferRef.current];
          pausedBufferRef.current = [];
          return next.slice(-MAX_LINES);
        });
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      reconnectTimerRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      const text = event.data as string;
      if (isPausedRef.current) {
        pausedBufferRef.current = [...pausedBufferRef.current, text].slice(-MAX_LINES);
        return;
      }
      setLines((prev) => [...prev, text].slice(-MAX_LINES));
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const clearLogs = useCallback(() => {
    setLines([]);
    pausedBufferRef.current = [];
  }, []);

  return { lines, isConnected, isPaused, togglePause, clearLogs };
}